import {
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger
} from "@fluentui/react-components";
import {
  ChevronDown20Regular,
  Circle20Filled,
  Dismiss20Regular,
  Highlight20Regular,
  Link20Regular,
  Table20Regular,
  TextBold20Regular,
  TextBulletList20Regular,
  TextItalic20Regular,
  TextNumberListLtr20Regular,
  TextUnderline20Regular
} from "@fluentui/react-icons";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  $createListItemNode,
  $createListNode,
  $isListItemNode,
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode
} from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getSelectionStyleValueForProperty } from "@lexical/selection";
import { mergeRegister } from "@lexical/utils";
import {
  $addUpdateTag,
  $createParagraphNode,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  ElementNode,
  FORMAT_TEXT_COMMAND,
  LexicalNode,
  RangeSelection,
  SELECTION_CHANGE_COMMAND
} from "lexical";

import { Tooltip } from "components/tooltip/Tooltip";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  ColorPicker,
  FALLBACK_TEXT_COLOR,
  useTextDarkColor
} from "./ColorPicker";
import { CHANGE_FONT_SIZE_COMMAND } from "./FontSizePlugin";
import { CHANGE_HIGHLIGHT_COLOR_COMMAND } from "./HighlightColorPlugin";
import { InsertLinkDialog } from "./LinkPlugin";
import { InsertTableDialog } from "./TablePlugin";
import { CHANGE_TEXT_COLOR_COMMAND } from "./TextColorPlugin";

function $splitListAtSelection(
  selection: RangeSelection,
  listNode: ListNode
): void {
  const allItems = listNode.getChildren();

  const getListItemAncestor = (node: LexicalNode): ListItemNode | null => {
    let n: LexicalNode | null = node;
    while (n) {
      if ($isListItemNode(n)) { return n as ListItemNode; }
      n = n.getParent();
    }
    return null;
  };

  const anchorItem = getListItemAncestor(selection.anchor.getNode());
  const focusItem = getListItemAncestor(selection.focus.getNode());

  if (!anchorItem || !focusItem) { return; }

  const anchorIdx = allItems.indexOf(anchorItem);
  const focusIdx = allItems.indexOf(focusItem);
  if (anchorIdx === -1 || focusIdx === -1) { return; }

  const firstIdx = Math.min(anchorIdx, focusIdx);
  const lastIdx = Math.max(anchorIdx, focusIdx);

  const itemsBefore = allItems.slice(0, firstIdx);
  const selectedItems = allItems.slice(firstIdx, lastIdx + 1);
  const itemsAfter = allItems.slice(lastIdx + 1);

  // Convert selected list items to plain paragraphs
  const paragraphs = (selectedItems as ListItemNode[]).map((item) => {
    const para = $createParagraphNode();
    item.getChildren().forEach((child) => para.append(child));
    return para;
  });

  if (itemsBefore.length > 0) {
    const beforeList = $createListNode(listNode.getListType());
    (itemsBefore as ListItemNode[]).forEach((item) => beforeList.append(item));
    listNode.insertBefore(beforeList);
  }

  paragraphs.forEach((para) => listNode.insertBefore(para));

  if (itemsAfter.length > 0) {
    const afterList = $createListNode(listNode.getListType());
    (itemsAfter as ListItemNode[]).forEach((item) => afterList.append(item));
    listNode.insertBefore(afterList);
  }

  listNode.remove();
}

/**
 * Convert all list items in the given lists to paragraphs
 */
function convertAllListItemsToParagraphs(lists: ListNode[]): void {
  for (const list of lists) {
    const items = list.getChildren() as ListItemNode[];
    for (const item of items) {
      const para = $createParagraphNode();
      item.getChildren().forEach((child) => para.append(child));
      list.insertBefore(para);
    }
    list.remove();
  }
}

/**
 * Convert all elements (lists and paragraphs) into a single unified list
 */
function convertAllToUnifiedList(
  elements: ElementNode[],
  listType: "bullet" | "number"
): void {
  if (elements.length === 0) { return; }

  // Create the unified list
  const unifiedList = $createListNode(listType);

  for (const element of elements) {
    if ($isListNode(element)) {
      // Move all list items to unified list
      const items = element.getChildren() as ListItemNode[];
      items.forEach((item) => unifiedList.append(item));
    } else if ($isParagraphNode(element)) {
      // Convert paragraph to list item
      const listItem = $createListItemNode();
      element.getChildren().forEach((child) => listItem.append(child));
      unifiedList.append(listItem);
    }
  }

  // Insert unified list before first element
  elements[0].insertBefore(unifiedList);

  // Remove original elements
  elements.forEach((el) => el.remove());
}

/**
 * Intelligent list toggle that handles multi-element selections.
 * If all selected elements are lists of the same type -> remove bullets
 * Otherwise -> merge/create unified list
 */
function $intelligentToggleList(
  selection: RangeSelection,
  listType: "bullet" | "number"
): void {
  const nodes = selection.getNodes();

  // Get unique top-level elements
  const topLevelElementsSet = new Set(
    nodes.map((n) => n.getTopLevelElement()).filter(Boolean)
  );
  const topLevelElements = Array.from(topLevelElementsSet) as ElementNode[];

  if (topLevelElements.length === 0) { return; }

  // Analyze selection
  const listElements = topLevelElements.filter($isListNode) as ListNode[];
  const allSameListType = listElements.every(
    (list) => list.getListType() === listType
  );
  const hasOnlyListsOfType =
    topLevelElements.length === listElements.length && allSameListType;

  if (hasOnlyListsOfType) {
    // STATE: All selected are lists of same type → REMOVE bullets
    convertAllListItemsToParagraphs(listElements);
  } else {
    // STATE: Mixed content → MERGE/CREATE unified list
    convertAllToUnifiedList(topLevelElements, listType);
  }
}

// Custom tags to identify toolbar-initiated updates
const FONT_SIZE_CHANGE_TAG = "toolbar-font-size-change";
const TEXT_COLOR_CHANGE_TAG = "toolbar-text-color-change";
const HIGHLIGHT_COLOR_CHANGE_TAG = "toolbar-highlight-color-change";

type ToolbarPluginProps = {
  toolbarId: string;
  minimalMode?: boolean;
};

export default function ToolbarPlugin({
  toolbarId,
  minimalMode = false
}: ToolbarPluginProps): React.JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isBulletList, setIsBulletList] = useState(false);
  const [isOrderedList, setIsOrderedList] = useState(false);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [showFontSizeDropdown, setShowFontSizeDropdown] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [baseFontSize, setBaseFontSize] = useState(16);
  const [textColor, setTextColor] = useState(FALLBACK_TEXT_COLOR);
  const [highlightColor, setHighlightColor] = useState("transparent");
  const baseTextColor = useTextDarkColor();
  const isApplyingFontSize = React.useRef(false);
  const lastAppliedFontSize = React.useRef<number | null>(null);
  const isApplyingColor = React.useRef(false);
  const lastAppliedColor = React.useRef<string | null>(null);
  const isApplyingHighlight = React.useRef(false);
  const lastAppliedHighlight = React.useRef<string | null>(null);

  const updateToolbar = useCallback(() => {
    // Skip update if we're currently applying changes
    if (
      isApplyingFontSize.current ||
      isApplyingColor.current ||
      isApplyingHighlight.current
    ) {
      return;
    }

    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));

      const node = selection.anchor.getNode();
      const parent = node.getParent();
      setIsLink($isLinkNode(parent) || $isLinkNode(node));

      // Check list type
      const element = node.getTopLevelElement();
      if (element && $isListNode(element)) {
        const listType = element.getListType();
        setIsBulletList(listType === "bullet");
        setIsOrderedList(listType === "number");
      } else {
        setIsBulletList(false);
        setIsOrderedList(false);
      }

      // Use Lexical's official API: reads selection.style first (pending format at caret),
      // then falls back to text node styles for range selections.
      let fontSizeFound = false;
      let colorFound = false;
      let highlightFound = false;

      const fontSizeStr = $getSelectionStyleValueForProperty(
        selection,
        "font-size",
        ""
      );
      const colorStr = $getSelectionStyleValueForProperty(
        selection,
        "color",
        ""
      );
      const bgColorStr = $getSelectionStyleValueForProperty(
        selection,
        "background-color",
        ""
      );

      const fontSizeNumericMatch = fontSizeStr.match(/^(\d+(?:\.\d+)?)(px)?$/);
      if (fontSizeNumericMatch) {
        const parsedSize = Number(fontSizeNumericMatch[1]);
        if (!Number.isNaN(parsedSize)) {
          setFontSize(parsedSize);
          fontSizeFound = true;
        }
      }

      if (colorStr) {
        setTextColor(colorStr);
        colorFound = true;
      }

      if (bgColorStr) {
        setHighlightColor(bgColorStr);
        highlightFound = true;
      }

      // If no font size found, use last applied value or fallback to base
      if (!fontSizeFound) {
        if (lastAppliedFontSize.current !== null) {
          setFontSize(lastAppliedFontSize.current);
        } else {
          setFontSize(baseFontSize);
        }
      }

      // If no color found, use last applied value or fallback to base
      if (!colorFound) {
        if (lastAppliedColor.current !== null) {
          setTextColor(lastAppliedColor.current);
        } else {
          setTextColor(baseTextColor);
        }
      }

      // If no highlight found, use last applied value or fallback to transparent
      if (!highlightFound) {
        if (lastAppliedHighlight.current !== null) {
          setHighlightColor(lastAppliedHighlight.current);
        } else {
          setHighlightColor("transparent");
        }
      }
    }
  }, [baseFontSize, baseTextColor]);

  useEffect(() => {
    // Get base font size from CSS variable
    const computedStyle = getComputedStyle(document.documentElement);
    const fontSizeValue = computedStyle
      .getPropertyValue("--input-font-size")
      .trim();
    if (fontSizeValue) {
      const parsedSize = parseInt(fontSizeValue, 10);
      if (!isNaN(parsedSize)) {
        setBaseFontSize(parsedSize);
      }
    }
  }, []);

  useEffect(() => {
    // Initialize text color from CSS variable
    if (baseTextColor !== FALLBACK_TEXT_COLOR) {
      setTextColor(baseTextColor);
    }
  }, [baseTextColor]);

  useEffect(() => {
    // Initialize toolbar state on mount
    editor.getEditorState().read(() => {
      updateToolbar();
    });

    // Register listeners for future updates
    return mergeRegister(
      editor.registerUpdateListener(({ editorState, tags }) => {
        // Skip toolbar update if this update was triggered by toolbar actions
        const isToolbarUpdate =
          tags.has(FONT_SIZE_CHANGE_TAG) ||
          tags.has(TEXT_COLOR_CHANGE_TAG) ||
          tags.has(HIGHLIGHT_COLOR_CHANGE_TAG);
        if (isToolbarUpdate) {
          // Reset the applying flags since the update is complete
          isApplyingFontSize.current = false;
          isApplyingColor.current = false;
          isApplyingHighlight.current = false;
          return;
        }
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        1
      )
    );
  }, [editor, updateToolbar]);

  const toggleBold = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
  };

  const toggleItalic = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
  };

  const toggleUnderline = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
  };

  const toggleLink = () => {
    if (!isLink) {
      setShowLinkDialog(true);
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  };

  const handleInsertLink = useCallback(
    (url: string) => {
      editor.focus();
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
    },
    [editor]
  );

  const toggleList = (
    listType: "bullet" | "number",
    insertCommand:
      | typeof INSERT_UNORDERED_LIST_COMMAND
      | typeof INSERT_ORDERED_LIST_COMMAND
  ) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        const anchorElement = anchorNode.getTopLevelElement();
        if (!anchorElement) {
          return
        };

        if (selection.isCollapsed()) {
          // COLLAPSED (cursor): Keep existing behavior
          if ($isListNode(anchorElement) && anchorElement.getListType() === listType) {
            $splitListAtSelection(selection, anchorElement);
          } else {
            editor.dispatchCommand(insertCommand, undefined);
          }
        } else {
          // RANGE SELECTION: Check if within single list or spans multiple elements
          const nodes = selection.getNodes();
          const topLevelElementsSet = new Set(
            nodes.map((n) => n.getTopLevelElement()).filter(Boolean)
          );
          const topLevelElements = Array.from(topLevelElementsSet);

          // If selection is within a single top-level element, use split behavior
          if (topLevelElements.length === 1) {
            const element = topLevelElements[0];
            if ($isListNode(element) && element.getListType() === listType) {
              $splitListAtSelection(selection, element as ListNode);
            } else {
              editor.dispatchCommand(insertCommand, undefined);
            }
          } else {
            // Selection spans multiple top-level elements: use intelligent toggle
            $intelligentToggleList(selection, listType);
          }
        }
      }
    });
  };

  const toggleBulletList = () =>
    toggleList("bullet", INSERT_UNORDERED_LIST_COMMAND);

  const toggleOrderedList = () =>
    toggleList("number", INSERT_ORDERED_LIST_COMMAND);

  const handleFontSizeChange = (newSize: number) => {
    // Set flag to prevent updateToolbar from overriding our change
    isApplyingFontSize.current = true;

    // Store the last applied font size
    lastAppliedFontSize.current = newSize;

    // Update state immediately
    setFontSize(newSize);

    // Close the dropdown
    setShowFontSizeDropdown(false);

    // Apply the font size command with a tag so the update listener can identify it
    editor.update(() => {
      $addUpdateTag(FONT_SIZE_CHANGE_TAG);
      editor.dispatchCommand(CHANGE_FONT_SIZE_COMMAND, `${newSize}px`);
    });
  };

  const handleColorChange = (color: string) => {
    // Set flag to prevent updateToolbar from overriding our change
    isApplyingColor.current = true;

    // Store the last applied color
    lastAppliedColor.current = color;

    // Update state immediately
    setTextColor(color);

    // Close the color picker
    setShowColorPicker(false);

    // Apply the color command with a tag so the update listener can identify it
    editor.update(() => {
      $addUpdateTag(TEXT_COLOR_CHANGE_TAG);
      editor.dispatchCommand(CHANGE_TEXT_COLOR_COMMAND, color);
    });
  };

  const handleHighlightColorChange = (color: string) => {
    // Set flag to prevent updateToolbar from overriding our change
    isApplyingHighlight.current = true;

    // Store the last applied highlight color
    lastAppliedHighlight.current = color;

    // Update state immediately
    setHighlightColor(color);

    // Close the highlight picker
    setShowHighlightPicker(false);

    // Apply the highlight command with a tag so the update listener can identify it
    editor.update(() => {
      $addUpdateTag(HIGHLIGHT_COLOR_CHANGE_TAG);
      editor.dispatchCommand(CHANGE_HIGHLIGHT_COLOR_COMMAND, color);
    });
  };

  const fontSizeOptions = React.useMemo(() => {
    const maxSize = baseFontSize + 20;
    const options: number[] = [];
    for (let size = baseFontSize; size <= maxSize; size += 2) {
      options.push(size);
    }
    return options;
  }, [baseFontSize]);

  return (
    <>
      <div id={toolbarId} className="editor-toolbar">
        {!minimalMode && (
          <>
            <Popover
              open={showFontSizeDropdown}
              onOpenChange={(_, data) => setShowFontSizeDropdown(data.open)}
              positioning="below"
            >
              <Tooltip content={<p className="m-0">Fontstorlek</p>}>
                <PopoverTrigger disableButtonEnhancement>
                  <button
                    className="font-size-dropdown-trigger"
                    aria-label="Fontstorlek"
                    type="button"
                    tabIndex={-1}
                  >
                    {fontSize}

                    <ChevronDown20Regular />
                  </button>
                </PopoverTrigger>
              </Tooltip>
              <PopoverSurface className="font-size-listbox">
                {fontSizeOptions.map((size) => (
                  <div
                    key={size}
                    className={`font-size-option ${fontSize === size ? "selected" : ""}`}
                    onClick={() => handleFontSizeChange(size)}
                  >
                    {size}
                  </div>
                ))}
              </PopoverSurface>
            </Popover>
            <Popover
              open={showColorPicker}
              onOpenChange={(_, data) => setShowColorPicker(data.open)}
              positioning="below"
            >
              <Tooltip content={<p className="m-0">Teckenfärg</p>}>
                <PopoverTrigger disableButtonEnhancement>
                  <button
                    className="color-picker-trigger"
                    aria-label="Teckenfärg"
                    type="button"
                    tabIndex={-1}
                  >
                    <Circle20Filled primaryFill={textColor} />
                  </button>
                </PopoverTrigger>
              </Tooltip>
              <PopoverSurface className="color-picker-surface">
                <ColorPicker
                  selectedColor={textColor}
                  onColorChange={handleColorChange}
                  title="Teckenfärg"
                />
              </PopoverSurface>
            </Popover>
            <Popover
              open={showHighlightPicker}
              onOpenChange={(_, data) => setShowHighlightPicker(data.open)}
              positioning="below"
            >
              <Tooltip content={<p className="m-0">Överstrykningsfärg</p>}>
                <PopoverTrigger disableButtonEnhancement>
                  <button
                    className="highlight-picker-trigger"
                    aria-label="Överstrykningsfärg"
                    type="button"
                    style={{
                      backgroundColor:
                        highlightColor !== "transparent"
                          ? highlightColor
                          : undefined
                    }}
                    tabIndex={-1}
                  >
                    <Highlight20Regular />
                  </button>
                </PopoverTrigger>
              </Tooltip>
              <PopoverSurface className="color-picker-surface">
                <ColorPicker
                  selectedColor={highlightColor}
                  onColorChange={handleHighlightColorChange}
                  title="Markeringsfärg"
                >
                  <Tooltip
                    content={<p className="m-0">Återställ färgval (vit)</p>}
                  >
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Dismiss20Regular />}
                      onClick={() => handleHighlightColorChange("#FFFFFF")}
                      aria-label="Återställ färgval (vit)"
                    >
                      Ingen färg
                    </Button>
                  </Tooltip>
                </ColorPicker>
              </PopoverSurface>
            </Popover>
          </>
        )}
        <Tooltip content={<p className="m-0">Fetstil</p>}>
          <button
            onClick={toggleBold}
            className={`toolbar-btn ${isBold ? "active" : ""}`}
            aria-label="Fetstil"
            type="button"
            tabIndex={-1}
          >
            <TextBold20Regular />
          </button>
        </Tooltip>
        <Tooltip content={<p className="m-0">Kursiv</p>}>
          <button
            onClick={toggleItalic}
            className={`toolbar-btn ${isItalic ? "active" : ""}`}
            aria-label="Kursiv"
            type="button"
            tabIndex={-1}
          >
            <TextItalic20Regular />
          </button>
        </Tooltip>


        <Tooltip content={<p className="m-0">Understruken</p>}>
          <button
            onClick={toggleUnderline}
            className={`toolbar-btn ${isUnderline ? "active" : ""}`}
            aria-label="Understruken"
            type="button"
            tabIndex={-1}
          >
            <TextUnderline20Regular />
          </button>
        </Tooltip>

        {!minimalMode && (
          <Tooltip content={<p className="m-0">Infoga tabell</p>}>
            <button
              onClick={() => setShowTableDialog(true)}
              className="toolbar-btn"
              aria-label="Infoga tabell"
              type="button"
              tabIndex={-1}
            >
              <Table20Regular />
            </button>
          </Tooltip>
        )}

        <Tooltip content={<p className="m-0">Punktlista</p>}>
          <button
            onClick={toggleBulletList}
            className={`toolbar-btn ${isBulletList ? "active" : ""}`}
            aria-label="Punktlista"
            type="button"
            tabIndex={-1}
          >
            <TextBulletList20Regular />
          </button>
        </Tooltip>

        <Tooltip content={<p className="m-0">Nummerlista</p>}>
          <button
            onClick={toggleOrderedList}
            className={`toolbar-btn ${isOrderedList ? "active" : ""}`}
            aria-label="Nummerlista"
            type="button"
            tabIndex={-1}
          >
            <TextNumberListLtr20Regular />
          </button>
        </Tooltip>

        {!minimalMode && (
          <Tooltip content={<p className="m-0">Länk</p>}>
            <button
              onClick={toggleLink}
              className={`toolbar-btn ${isLink ? "active" : ""}`}
              aria-label="Länk"
              type="button"
              tabIndex={-1}
            >
              <Link20Regular />
            </button>
          </Tooltip>
        )}
      </div>
      {!minimalMode &&
        showTableDialog &&
        createPortal(
          <InsertTableDialog onClose={() => setShowTableDialog(false)} />,
          document.body
        )}
      {!minimalMode &&
        showLinkDialog &&
        createPortal(
          <InsertLinkDialog
            onClose={() => setShowLinkDialog(false)}
            onInsert={handleInsertLink}
          />,
          document.body
        )}
    </>
  );
}
